import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  toErrorMessage,
  useElementClickedTracking,
  useErrorTracking,
} from "@/lib/analytics/useTracking";
import { titleQueryKeys } from "@/lib/react-query/keyFactory";
import { participantAdd, participantRemove } from "@/proofingService-client/sdk.gen";
import type { ParticipantAddResponse } from "@/proofingService-client/types.gen";

interface ParticipantsCache {
  data: ParticipantAddResponse[];
  [key: string]: unknown;
}

export interface UseAddParticipantOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for adding a participant to a proofing title.
 * Handles API calls, loading states, error handling, and cache invalidation.
 *
 * @param options - Optional callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 */
export const useAddParticipant = (options?: UseAddParticipantOptions) => {
  const queryClient = useQueryClient();
  const trackElementClicked = useElementClickedTracking();
  const trackError = useErrorTracking();

  return useMutation({
    mutationFn: async ({ titleId, userId }: { titleId: string; userId: string }) => {
      const response = await participantAdd({
        path: { titleId },
        body: { userId },
      });
      if (!response.data) {
        throw new Error("Failed to add participant");
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Optimistically update participants cache
      queryClient.setQueryData(
        titleQueryKeys.participants(variables.titleId),
        (old: ParticipantsCache | undefined) => {
          if (!old?.data) return old;

          // Check if participant already exists (might be soft-deleted)
          const existingIndex = old.data.findIndex((p) => p.userId === data.userId);

          let updatedData: ParticipantAddResponse[];
          if (existingIndex !== -1) {
            // Update existing participant (undelete it)
            updatedData = [...old.data];
            updatedData[existingIndex] = { ...data, deleted: false };
          } else {
            // Add new participant
            updatedData = [...old.data, data];
          }

          return {
            ...old,
            data: updatedData,
          };
        },
      );

      // Also invalidate title details to refresh round participants
      queryClient.invalidateQueries({
        queryKey: titleQueryKeys.detail(variables.titleId),
      });

      // Invalidate project permissions since adding a participant affects authorization
      queryClient.invalidateQueries({
        queryKey: ["permissions", "project"],
      });
      trackElementClicked({ elementId: "participant-added" });
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error("Failed to add participant to proofing title:", error);
      trackError({ type: "participant-error", action: "add", error: toErrorMessage(error) });
      options?.onError?.(error);
    },
  });
};

export interface UseRemoveParticipantOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for removing a participant from a proofing title.
 * Handles API calls, loading states, error handling, and cache invalidation.
 *
 * @param options - Optional callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 */
export const useRemoveParticipant = (options?: UseRemoveParticipantOptions) => {
  const queryClient = useQueryClient();
  const trackElementClicked = useElementClickedTracking();
  const trackError = useErrorTracking();

  return useMutation({
    mutationFn: async ({ titleId, userId }: { titleId: string; userId: string }) => {
      const response = await participantRemove({
        path: { titleId, userId },
      });
      if (!response.data) {
        throw new Error("Failed to remove participant");
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Optimistically update participants cache (soft delete)
      queryClient.setQueryData(
        titleQueryKeys.participants(variables.titleId),
        (old: ParticipantsCache | undefined) => {
          if (!old?.data) return old;

          // Mark the participant as deleted (soft delete)
          return {
            ...old,
            data: old.data.map((p) =>
              p.userId === variables.userId ? { ...p, deleted: true } : p,
            ),
          };
        },
      );

      // Also invalidate title details to refresh round participants
      queryClient.invalidateQueries({
        queryKey: titleQueryKeys.detail(variables.titleId),
      });

      // Invalidate project permissions since removing a participant affects authorization
      // This ensures the UI updates immediately (e.g., hiding "Review PDF" button if user removed themselves)
      queryClient.invalidateQueries({
        queryKey: ["permissions", "project"],
      });
      trackElementClicked({ elementId: "participant-removed" });
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error("Failed to remove participant from proofing title:", error);
      trackError({ type: "participant-error", action: "remove", error: toErrorMessage(error) });
      options?.onError?.(error);
    },
  });
};
